import { useTranslation } from "react-i18next";
import type { CostAmounts, ExpectedGroupHours, ExpectedRule, ExpectedToDate } from "@torre/work-hours";
import { gapTone } from "../theme";
import { moneyOfAmounts, type Money } from "../lib/ledger";
import { ForeignPart, MoneyText } from "./LedgerParts";

// ---------------------------------------------------------------------------
// Esperado a hoy vs real (#969): cuántas horas DEBERÍA llevar el equipo (o la
// persona) hasta hoy según capacidad y asignaciones, contra las que cuentan.
// Regla: capacidad / 5 por día hábil × % de asignación, desde el inicio de la
// asignación y cortado a hoy. Sin contratistas ni personal sin GitHub.
// Las mismas horas por squad (con «Sin squad») y por proyecto ya no van debajo
// del total: son pestañas del desglose del tablero (`ExpectedBreakdownTable`).
// Los KPIs solo traen HORAS: lo que valen en dinero es de los dueños
// (ExpectedCostCard). En las pestañas, los dueños ven además el costo de lo
// trabajado de cada squad o proyecto, SIEMPRE con su equivalente en MXN, y por
// squad la pérdida por horas faltantes contra el esperado a hoy.
//
// #1281: las entradas manuales de más de 1 h esperan al tech lead y NO están
// en «Real». Debajo de «Real» va «Por aprobar: X h» y, en «Diferencia», una
// segunda línea «+ Horas por aprobar» = real + por aprobar − esperado: lo que
// quedaría si el tech lead aprobara todo lo pendiente.
// ---------------------------------------------------------------------------

const fmt = (n: number) => `${Math.round(n * 100) / 100} h`;
const signed = (n: number) => `${n > 0 ? "+" : ""}${fmt(n)}`;

/** ¿El backend ya manda las horas por aprobar (#1281)? Sin el campo, no se pinta nada. */
export const hasPending = (e: Pick<ExpectedToDate, "teamPendingHours"> | null | undefined): boolean => typeof e?.teamPendingHours === "number";

/** Importe con el código de moneda al final: MXN y USD usan el mismo «$». */
export const money = (value: number, currency: string, language = "es") =>
  `${new Intl.NumberFormat(String(language).startsWith("en") ? "en-US" : "es-MX", {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 0,
  }).format(value)} ${currency}`;

/** Importes `{ MXN: 1200, USD: 50 }` separados por moneda (#1063: costo por repo). */
export const moneyAmounts = (byCurrency: Record<string, number>, language = "es") =>
  Object.keys(byCurrency)
    .sort()
    .map((c) => money(byCurrency[c], c, language))
    .join(" · ");

/** Diferencia real − esperado con el tono de estado: negativo = faltante. */
export function GapBadge({ gapHours, testId }: { gapHours: number; testId?: string }) {
  const { t } = useTranslation("common");
  const abs = Math.round(Math.abs(gapHours) * 100) / 100;
  const label = gapHours < 0 ? t("workHours.expected.short", { hours: abs }) : gapHours > 0 ? t("workHours.expected.over", { hours: abs }) : t("workHours.expected.onTrack");
  return (
    <span className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium ${gapTone(gapHours)}`} data-testid={testId} data-gap={gapHours}>
      {label}
    </span>
  );
}

/**
 * Segunda línea de una diferencia (#1281): «+ Horas por aprobar: ±N h», con el
 * tono de lo que quedaría si se aprobara todo lo pendiente.
 */
export function GapWithPending({ gapWithPendingHours, pendingHours, testId }: { gapWithPendingHours: number; pendingHours: number; testId?: string }) {
  const { t } = useTranslation("common");
  const hint = t("workHours.expected.gapWithPendingHint");
  return (
    <span className="mt-0.5 block whitespace-nowrap text-[11px]" title={hint} data-testid={testId} data-gap={gapWithPendingHours} data-pending={pendingHours}>
      <span className="opacity-70">{t("workHours.expected.gapWithPending")}:</span>{" "}
      <span className={`rounded px-1 py-0.5 font-medium ${gapTone(gapWithPendingHours)}`}>{signed(gapWithPendingHours)}</span>
    </span>
  );
}

/** Texto de la regla (tooltip). */
export function useExpectedRuleText(rule: ExpectedRule | null | undefined): string {
  const { t } = useTranslation("common");
  if (!rule) return "";
  return `${t("workHours.expected.rule", { hoursPerDay: rule.hoursPerDay })} ${t("workHours.expected.ruleRange", {
    days: rule.businessDays,
    from: rule.from,
    to: rule.to,
  })}`;
}

interface Props {
  expected: ExpectedToDate;
  /**
   * Horas del KPI «Horas» que NO están en «Real»: las de quien tiene horas en el
   * rango pero no entra al esperado (sin asignación, sin GitHub…). Con nombres.
   */
  outside?: { hours: number; names: string[] };
  cardClass: string;
  textClass: string;
  secondaryClass: string;
}

export default function ExpectedKpis({ expected, outside, cardClass, textClass, secondaryClass }: Props) {
  const { t } = useTranslation("common");
  const ruleText = useExpectedRuleText(expected.rule);
  const pending = hasPending(expected);
  const kpis = [
    { key: "expected", label: t("workHours.expected.expected"), value: fmt(expected.teamExpectedHours), icon: "ri-calendar-check-line" },
    { key: "actual", label: t("workHours.expected.actual"), value: fmt(expected.teamActualHours), icon: "ri-time-line" },
    { key: "gap", label: t("workHours.expected.gap"), value: signed(expected.gapHours), icon: "ri-scales-3-line" },
  ];
  return (
    <div data-testid="expected-kpis">
      <p className={`mb-2 flex items-center gap-1 text-sm font-semibold ${textClass}`}>
        {t("workHours.expected.title")}
        <i className="ri-information-line cursor-help text-xs opacity-70" title={ruleText} aria-label={ruleText} data-testid="expected-rule" />
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {kpis.map((k) => (
          <div key={k.key} className={cardClass} data-testid={`expected-kpi-${k.key}`}>
            <p className={`flex items-center gap-1 text-xs ${secondaryClass}`}>
              <i className={k.icon} /> {k.label}
            </p>
            <p className={`mt-1 text-2xl font-bold ${k.key === "gap" ? (expected.gapHours < 0 ? "text-red-500" : "text-emerald-500") : textClass}`}>{k.value}</p>
            {k.key === "gap" && (
              <div className="mt-0.5">
                <GapBadge gapHours={expected.gapHours} testId="expected-gap-badge" />
                {/* Solo si HAY horas por aprobar: con 0 sería la misma diferencia repetida
                    bajo un rótulo que hace creer que hay algo pendiente. */}
                {pending && expected.teamPendingHours > 0 && (
                  <GapWithPending gapWithPendingHours={expected.gapWithPendingHours} pendingHours={expected.teamPendingHours} testId="expected-gap-with-pending" />
                )}
              </div>
            )}
            {k.key === "actual" && pending && expected.teamPendingHours > 0 && (
              <p className={`mt-0.5 flex items-center gap-1 text-[11px] text-amber-600`} title={t("workHours.expected.pendingHint")} data-testid="expected-pending">
                <i className="ri-hourglass-line" />
                {t("workHours.expected.pendingHours", { hours: Math.round(expected.teamPendingHours * 100) / 100 })}
              </p>
            )}
            {/* «Real» solo suma a quien entra al esperado. Si el KPI de horas del
                tablero trae además horas de otras personas, se dice cuántas y de quién. */}
            {k.key === "actual" && outside && outside.hours > 0 && (
              <p className={`mt-0.5 text-[11px] ${secondaryClass}`} data-testid="expected-outside">
                {t("workHours.expected.outside", { hours: Math.round(outside.hours * 100) / 100, names: outside.names.join(", ") })}
              </p>
            )}
            {k.key === "expected" && <p className={`mt-0.5 text-[11px] ${secondaryClass}`}>{t("workHours.expected.ruleRange", { days: expected.rule.businessDays, from: expected.rule.from, to: expected.rule.to })}</p>}
          </div>
        ))}
      </div>
      {expected.nonTrackedMembers.length > 0 && (
        <p className={`mt-2 text-[11px] ${secondaryClass}`} title={t("workHours.expected.nonTrackedHint")} data-testid="expected-non-tracked">
          <i className="ri-user-unfollow-line mr-1" />
          {t("workHours.expected.nonTracked", { names: expected.nonTrackedMembers.map((m) => m.name).join(", ") })}
        </p>
      )}
    </div>
  );
}

interface ExpectedBreakdownTableProps {
  expected: ExpectedToDate;
  by: "squad" | "project";
  textClass: string;
  secondaryClass: string;
}

/**
 * Las mismas horas del esperado, por squad (con «Sin squad») o por proyecto.
 * Vive en las pestañas «Por squad» / «Por proyecto» del desglose del tablero:
 * no trae tarjeta ni título propios, los pone la pestaña.
 */
export function ExpectedBreakdownTable({ expected, by, textClass, secondaryClass }: ExpectedBreakdownTableProps) {
  const { t } = useTranslation("common");
  const breakdown = expected.breakdown;
  if (!breakdown) return null;
  const total: ExpectedGroupHours = {
    people: expected.byPerson.length,
    expectedHours: expected.teamExpectedHours,
    actualHours: expected.teamActualHours,
    gapHours: expected.gapHours,
    pendingHours: expected.teamPendingHours ?? 0,
    gapWithPendingHours: expected.gapWithPendingHours ?? expected.gapHours,
  };
  // Costo de lo trabajado por fila. Solo dueños: `cost` no viaja a nadie más.
  const cost = expected.cost;
  const amountOf = (field: "actualCost" | "lossCost", byCurrency: Record<string, CostAmounts> | undefined, unpriced: number): Money =>
    moneyOfAmounts(Object.fromEntries(Object.entries(byCurrency ?? {}).map(([c, a]) => [c, a[field]])), cost?.fx, unpriced > 0);
  const workedOf = (byCurrency: Record<string, CostAmounts> | undefined, unpriced: number): Money => amountOf("actualCost", byCurrency, unpriced);
  const costLines = new Map<string, Money>(
    by === "squad"
      ? (cost?.bySquad ?? []).map((s) => [s.squadId ?? "none", workedOf(s.byCurrency, s.unpricedPeople)])
      : (cost?.byProject ?? []).map((p) => [p.projectId, workedOf(p.byCurrency, p.unpricedPeople)]),
  );
  const moneyOf = cost ? (key: string) => costLines.get(key) ?? workedOf(undefined, 0) : undefined;
  const totalMoney = cost ? workedOf(cost.byCurrency, cost.unpricedMembers.length) : undefined;
  // Pérdida a hoy: Σ por persona de las horas que le faltan para el esperado × su
  // costo por hora (`lossCost`). Solo por squad: cada persona cuenta en uno, así
  // que las filas suman el total; por proyecto la pérdida es otra cuenta.
  const lossLines = new Map<string, Money>((cost?.bySquad ?? []).map((s) => [s.squadId ?? "none", amountOf("lossCost", s.byCurrency, s.unpricedPeople)]));
  const lossOf = cost && by === "squad" ? (key: string) => lossLines.get(key) ?? amountOf("lossCost", undefined, 0) : undefined;
  const totalLoss = cost && by === "squad" ? amountOf("lossCost", cost.byCurrency, cost.unpricedMembers.length) : undefined;
  const rows =
    by === "squad"
      ? breakdown.bySquad.map((s) => ({ key: s.squadId ?? "none", label: s.squadName ?? t("workHours.expected.noSquad"), muted: s.squadId === null, line: s as ExpectedGroupHours }))
      : breakdown.byProject.map((p) => ({ key: p.projectId, label: p.projectName, muted: false, line: p as ExpectedGroupHours }));
  return (
    <HoursTable
      head={t(by === "squad" ? "workHours.expected.squad" : "workHours.expected.project")}
      testId={by === "squad" ? "expected-by-squad" : "expected-by-project"}
      rows={rows}
      total={total}
      pending={hasPending(expected)}
      moneyOf={moneyOf}
      totalMoney={totalMoney}
      lossOf={lossOf}
      totalLoss={totalLoss}
      textClass={textClass}
      secondaryClass={secondaryClass}
    />
  );
}

interface HoursTableProps {
  /** Encabezado de la primera columna (Squad / Proyecto). */
  head: string;
  testId: string;
  rows: { key: string; label: string; muted: boolean; line: ExpectedGroupHours }[];
  total: ExpectedGroupHours;
  /** #1281: pintar la columna «Por aprobar» y la segunda línea de la diferencia. */
  pending: boolean;
  /** Costo de lo trabajado de una fila (solo dueños); sin él la columna no existe. */
  moneyOf?: (key: string) => Money;
  totalMoney?: Money;
  /** Pérdida por horas faltantes de una fila (solo dueños, por squad); sin ella la columna no existe. */
  lossOf?: (key: string) => Money;
  totalLoss?: Money;
  textClass: string;
  secondaryClass: string;
}

/** Esperado · real · (por aprobar) · diferencia por fila (squad o proyecto), con el total al pie. */
function HoursTable({ head, testId, rows, total, pending, moneyOf, totalMoney, lossOf, totalLoss, textClass, secondaryClass }: HoursTableProps) {
  const { t } = useTranslation("common");
  const moneyCell = (money: Money | undefined, rowId: string) =>
    money && (
      <td className="whitespace-nowrap py-1.5 pl-2 text-right align-top" data-testid={`${rowId}-cost`}>
        <MoneyText money={money} />
        <ForeignPart money={money} className={secondaryClass} />
      </td>
    );
  const lossCell = (loss: Money | undefined, rowId: string) =>
    loss && (
      <td className="whitespace-nowrap py-1.5 pl-2 text-right align-top" data-testid={`${rowId}-loss`}>
        <MoneyText money={loss} className={loss.empty ? "" : "font-semibold text-red-500"} />
        <ForeignPart money={loss} className={secondaryClass} />
      </td>
    );
  const cells = (line: ExpectedGroupHours, rowId: string) => (
    <>
      <td className="py-1.5 text-right">{fmt(line.expectedHours)}</td>
      <td className="py-1.5 text-right">{fmt(line.actualHours)}</td>
      {pending && (
        <td className="py-1.5 text-right text-amber-600" data-testid={`${rowId}-pending`}>
          {fmt(line.pendingHours ?? 0)}
        </td>
      )}
      <td className="py-1.5 text-right">
        <GapBadge gapHours={line.gapHours} />
        {pending && (line.pendingHours ?? 0) > 0 && <GapWithPending gapWithPendingHours={line.gapWithPendingHours ?? line.gapHours} pendingHours={line.pendingHours ?? 0} />}
      </td>
    </>
  );
  return (
    <div data-testid={testId}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={`text-left text-xs ${secondaryClass}`}>
              <th className="py-1">{head}</th>
              <th className="py-1 text-right">{t("workHours.expected.people")}</th>
              <th className="py-1 text-right">{t("workHours.expected.expected")}</th>
              <th className="py-1 text-right">{t("workHours.expected.actual")}</th>
              {pending && (
                <th className="py-1 text-right" title={t("workHours.expected.pendingHint")}>
                  {t("workHours.expected.pending")}
                </th>
              )}
              <th className="py-1 text-right">{t("workHours.expected.gap")}</th>
              {moneyOf && <th className="py-1 pl-2 text-right">{t("workHours.people.workedCost")}</th>}
              {lossOf && (
                <th className="py-1 pl-2 text-right" title={t("workHours.expected.cost.lossHint")}>
                  {t("workHours.people.lossCost")}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className={`border-t border-gray-500/20 ${r.muted ? secondaryClass : textClass}`} data-testid={`${testId}-${r.key}`}>
                <td className="py-1.5">{r.label}</td>
                <td className="py-1.5 text-right">{r.line.people}</td>
                {cells(r.line, `${testId}-${r.key}`)}
                {moneyCell(moneyOf?.(r.key), `${testId}-${r.key}`)}
                {lossCell(lossOf?.(r.key), `${testId}-${r.key}`)}
              </tr>
            ))}
            <tr className={`border-t border-gray-500/40 font-semibold ${textClass}`} data-testid={`${testId}-total`}>
              <td className="py-1.5">{t("workHours.expected.total")}</td>
              <td className="py-1.5 text-right">{total.people}</td>
              {cells(total, `${testId}-total`)}
              {moneyCell(moneyOf ? totalMoney : undefined, `${testId}-total`)}
              {lossCell(lossOf ? totalLoss : undefined, `${testId}-total`)}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
